import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { CalendarDays, ClipboardCheck, ListChecks } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const [monthly, setMonthly] = useState(null);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ summary: "", tasks_done: "", blockers: "" });

  const load = async () => {
    const [m, r] = await Promise.all([api.get("/reports/monthly"), api.get("/reports/daily")]);
    setMonthly(m.data); setReports(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.summary.trim()) return;
    try {
      await api.post("/reports/daily", form);
      toast.success("Daily report submitted");
      setForm({ summary: "", tasks_done: "", blockers: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const statusData = monthly ? [
    { name: "Pending", value: monthly.by_status.Pending, color: "hsl(45 100% 50%)" },
    { name: "In Progress", value: monthly.by_status["In Progress"], color: "hsl(218 100% 33%)" },
    { name: "Completed", value: monthly.by_status.Completed, color: "hsl(160 84% 39%)" },
  ] : [];

  return (
    <>
      <PageHeader
        title="Reporting"
        subtitle="Submit your daily work log and review monthly task progress against department targets."
        testId="reports-page"
      />

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-6">
          <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 mb-4">
            <ClipboardCheck className="h-5 w-5 text-primary" /> Today's Report
          </h3>
          <div className="space-y-4">
            <div><Label>What did you work on today?</Label>
              <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3}
                className="mt-1 rounded-sm" placeholder="Summary of today's work…" data-testid="report-summary-input" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Tasks completed</Label>
                <Textarea value={form.tasks_done} onChange={(e) => setForm({ ...form, tasks_done: e.target.value })} rows={2}
                  className="mt-1 rounded-sm" placeholder="Completed tasks…" /></div>
              <div><Label>Blockers</Label>
                <Textarea value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} rows={2}
                  className="mt-1 rounded-sm" placeholder="Any blockers…" /></div>
            </div>
            <Button onClick={submit} disabled={!form.summary.trim()} className="rounded-sm font-bold uppercase text-xs tracking-wide" data-testid="submit-report-button">
              Submit report
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Task Overview</h3>
          {monthly && (
            <>
              <StatCard label="Total Tasks" value={monthly.total_tasks} icon={ListChecks} />
              <ResponsiveContainer width="100%" height={160} className="mt-4">
                <BarChart data={statusData}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip cursor={{ fill: "hsl(210 16% 93%)" }} />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>

      {monthly && monthly.dept_progress.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-6 mb-6">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Department Target Progress</h3>
          <div className="space-y-4">
            {monthly.dept_progress.map((d) => (
              <div key={d.department}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{d.department}</span>
                  <span className="text-muted-foreground">{d.completed}/{d.total} completed · {d.progress}%</span>
                </div>
                <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${d.progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border bg-card rounded-sm p-6">
        <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 mb-4">
          <CalendarDays className="h-5 w-5 text-primary" /> Recent Daily Reports
        </h3>
        {reports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reports submitted yet.</p>
        ) : (
          <div className="space-y-3">
            {reports.slice(0, 10).map((r) => (
              <div key={r.id} className="border border-border rounded-sm p-4" data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-sm">{r.user_name}</p>
                  <p className="text-xs text-muted-foreground">{r.date}</p>
                </div>
                <p className="text-sm whitespace-pre-wrap">{r.summary}</p>
                {r.tasks_done && <p className="text-xs text-success mt-1">✓ {r.tasks_done}</p>}
                {r.blockers && <p className="text-xs text-destructive mt-1">⚠ {r.blockers}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
